import * as yup from 'yup';
import {
  basicPaymentRule,
  primaryPhoneRule,
  b2cPaymentRule,
} from '../../../utils/validation-rules/payment-rule';
import { isB2BUser } from '../../../utils/user';
import { IB2BPaymentCardsResponse } from '../../../models/b2b/payments';
import {
  ORANGE_PAY_URL_PROD,
  ORANGE_PAY_URL_STG,
} from '../../../constants/links';
import {
  ICreditCardBrand,
  TAddCardRequest,
  TPaymentFormValuesAddCard,
} from '../../../models/common/payments';
import { TModalCardData } from '../../shared/default-credit-card-modal/default-credit-card-modal';
import { getEnvironment } from '../../../utils/general';
import { Environment } from '../../../constants/general';

const environment = getEnvironment();

const getAncestorOrigins = () => {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
};

export const orangePayConsts = {
  url:
    environment === Environment.PROD ? ORANGE_PAY_URL_PROD : ORANGE_PAY_URL_STG,
  clientId: 'HDCOM_MYACCOUNT',
  storeNumber: environment === Environment.PROD ? '8119' : '9064',
  storeZipCode: '30339',
  amount: 1,
  countryCode: 'US',
  languageCode: 'en-US',
  orderNumber: '12345',
  billingAddress: {},
  transactionOptions: {
    tokenizeOnly: true,
  },
  cardBrokerEnabled: true,
  saqAEnabled: true,
  saveCard: false,
  ancestorOrigins: getAncestorOrigins(),
};

export const cssStyles =
  '.flex-container {padding: 0 1px 0 0 !important;}' +
  '.form-input--error .form-input__field {' +
  "   background-image: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAx" +
  'NiAxNiIgZmlsbD0iI0M2NEM0QyIgZm9jdXNhYmxlPSJmYWxzZSIgYXJpYS1oaWRkZW49InRy' +
  'dWUiPjxwYXRoIGQ9Ik0xNS4zMzQgMC42NjZIMC42NjZ2MTQuNjY2aDE0LjY2NnpNOC43MzQg' +
  'NC4wODh2NS4zNzhINy4yNjdWNC4wODl6bTAgNy4wODlhMC43MzQgMC43MzQgMCAxIDEgLTEu' +
  "NDY2IDAgMC43MzQgMC43MzQgMCAwIDEgMS40NjYgMCIvPjwvc3ZnPg==');" +
  '   background-color: initial;' +
  '   background-repeat: no-repeat;' +
  '   background-size: 16px 16px;' +
  '   border: 1px solid rgb(198 76 76 / 1) !important; ' +
  '   height: 44px !important;' +
  '}' +
  '.label { font-family: helvetica-neue-55-roman, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"; font-size: 1.6rem; color: rgb(33 33 32 / 1) !important; }' +
  '.drop-down__title {' +
  '   border: 1px solid #999; height: 44px !important;' +
  '   border-radius: 0.2rem;' +
  '   border-color: rgb(122 121 120 / 1);' +
  '} ' +
  '.flex-container .form-input__field {' +
  '    height: 44px;' +
  '    border-bottom: 1px solid #999;' +
  '    border-radius: 0.2rem;' +
  '    border-color: rgb(122 121 120 / 1);' +
  '}' +
  '.form-input--error .form-input-error__message, .expiration-date .drop-down-error__message {' +
  '   color: #C64C4C !important;' +
  '   line-height: 1;' +
  '   font-size: 1.2rem;' +
  '   font-family: helvetica-neue-55-roman, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' +
  '}' +
  '.expiration-date .selectors .drop-down__select{' +
  '   padding-top: 3px;' +
  '}' +
  '.drop-down__title--select::before {top:5px !important;}' +
  '.drop-down__title::after{top: 2px !important;}';

export const validationSchema = yup.object().shape({
  ...basicPaymentRule,
  ...primaryPhoneRule,
});

export const b2cValidationSchema = yup.object().shape({ ...b2cPaymentRule });

export const getCreditCardFormValuesAddCard = (
  isDefault: boolean,
): TPaymentFormValuesAddCard => ({
  isB2B: isB2BUser(),
  cardNickName: '',
  cardBrand: '',
  cardExpiryMonth: '',
  cardExpiryYear: '',
  cardHolderName: '',
  addressLine1: '',
  addressLine2: '',
  zipCode: '',
  cityState: '',
  buyerId: '',
  primaryPhone: {
    number: '',
  },
  isDefaultAddress: false,
  isHDWalletFlow: false,
  isHDCard: false,
  accountNumberXref: '',
  avsCode: '',
  cvvCode: '',
  addressId: '',
  billingAddressID: null,
  recaptcha: '',
  isDefault,
});
export interface IAddCreditCardForm {
  onAddCardCancel: () => void;
  onAddCardSuccess: (addCardResponse: IB2BPaymentCardsResponse) => void;
  isFirstCard: boolean;
  containerError: string | null;
}

export type TOrangePayInfoResponse = {
  reasonCode: string;
  description: string;
  sessionUuid: string;
};

export type TOrangePayResponse = {
  sessionId: string;
  orderNumber: string;
  credit?: TCardResponseMicroUi;
};

export type TCardResponseMicroUi = {
  cardReference: TCardReference;
  cardDetails: TCardDetails;
  posEntryMode: string;
  uuid?: string;
  amountRequested?: number;
  purchaseOrderRequired?: boolean;
};

export type TCardDetails = {
  reportPaymentMethodCode: string; // 'VI'
  creditTenderTypeId: string;
  cardClass: string;
  creditHostId: string;
  issuedCountryCode: string;
  paymentMethodCode: string;
  accountNumberXref: string;
  paymentToken: string;
  bin: string;
  panSuffix: string;
  isGSA?: boolean;
  isPrepaid: boolean;
  authorizedBuyerID?: string;
  expirationMonth: string;
  expirationYear: string;
  cardExpirationDate?: string;
};

type TCardReferenceSAQD = {
  keyId: string;
  encryptionType: string;
  encryptionBlock: string;
  encryptionBlockFields: string;
  cardSecurityIndicator: string;
};

type TCardReferenceSAQA = {
  cardCaptureSessionID: string;
};

export type TCardReference = TCardReferenceSAQA | TCardReferenceSAQD;

// no longer used, can be deleted in a later change
export type TCreditCardFormValues = {
  zipCode: string;
  city: string;
  state: string;
  cardHolderName: string;
  isHDWalletFlow: boolean;
  isDefaultAddress: boolean;
  buyerId: string;
  piePhase: string;
  primaryPhone: { number: string };
  isDefault: boolean;
  cardNickName: string;
  pieKeyId: string;
  isB2B: boolean;
  addressLine1: string;
  addressLine2: string;
  cardNumber: string;
  cityState: string;
};

export const collateOrangePayResponses: { [key: string]: number[] } = {
  NETWORK_ERR: [2002, 2003, 2007, 2008, 2014, 2016, 2019, 5080, 5082],
  UI_LOADED: [2004, 2005, 2018],
  VALID_FORM: [3052],
  INVALID_FORM: [3051, 3050, 3040],
  INVALID_PAYMENT: [5010, 5011, 5012, 5020],
  FISERV_FAIL: [2023],
  INFO: [1000, 2006, 2015],
  CARD_NOT_FOUND: [3021],
  SUBMIT_ERROR: [5030, 5040, 5071, 5073, 5074, 5085],
};

const orangePayResponses: { [key: string]: number[] } = {
  NETWORK_ERR: [
    2002, 2003, 2007, 2008, 2014, 2016, 2019, 5030, 5040, 5071, 5072, 5073,
    5074, 5080, 5082, 5085,
  ],
  UI_LOADED: [2004, 2005, 2018],
  VALID_FORM: [3052],
  INVALID_FORM: [3051, 3050, 3040],
  INVALID_PAYMENT: [5010, 5011, 5012, 3021],
  DECLINED: [5020],
  FISERV_FAIL: [2023],
  INFO: [1000, 2006, 2015],
};
export const getOrangePayResponse = (
  { reasonCode }: TOrangePayInfoResponse,
  isCollateReasonCodesEnabled = false,
) => {
  const responses = isCollateReasonCodesEnabled
    ? collateOrangePayResponses
    : orangePayResponses;

  const responseKey = Object.keys(responses).find((key) =>
    responses[key].includes(parseInt(reasonCode, 10)),
  );
  return responseKey || null;
};
export const mapDefaultModal = (
  cardDetails: TAddCardRequest,
): TModalCardData => {
  const cardBrand = cardDetails.cardBrand;
  const cardExpiryMonth =
    cardBrand === 'PROX' ? '' : cardDetails.cardExpiryMonth || '';
  const cardExpiryYear =
    cardBrand === 'PROX' ? '' : cardDetails.cardExpiryYear || '';
  const cardNumberLast4 = cardDetails.xrefCardNumber.slice(-4);

  return { cardBrand, cardExpiryMonth, cardExpiryYear, cardNumberLast4 };
};

export const OrangePayCardBrandFilterValue: Record<string, ICreditCardBrand> = {
  VI: 'VISA',
  MA: 'MA',
  AX: 'AMEX',
  DS: 'DISC',
  HR: 'HDCOM',
  HC: 'HDCON',
  HP: 'PROX',
  HA: 'PROP',
} as const;

export const ORANGE_PAY_NO_RESPONSE_TIMEOUT = 20000;
export const NEW_RELIC_PREFIX = 'add-card-pcir';
